import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Loader2, PenLine, XCircle } from "lucide-react";
import { api, errMsg, money, fdate } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { PageHeader, StatusBadge, Loading, Empty, DemoBadge, Timeline, Hash, Row, LegalPanel } from "@/components/common";

const TL = { iniciado: "Iniciado", pago_pendiente: "Pago pendiente", en_revision: "En revisión", emitido: "Emitido", rechazado: "Rechazado" };

export default function Expedientes() {
  const list = useFetch("/intranet/expedientes", { poll: 10000 });
  const [sel, setSel] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const cur = (list.data || []).find((e) => e.folio === sel);

  const act = async (action) => {
    setBusy(true);
    try {
      await api.patch(`/intranet/expedientes/${sel}`, { action, note });
      toast.success(action === "aprobar" ? "Documento firmado, sellado NOM-151 y anclado" : "Expediente rechazado");
      setNote(""); list.reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader eyebrow="Ventanilla · back office" title="Expedientes digitales" subtitle="Dictamina trámites pagados. Al aprobar, el documento se firma con Firma Electrónica Avanzada, se sella NOM-151 y se ancla en blockchain." />
      <Card><CardContent className="p-4">
        {list.loading ? <Loading /> : !list.data?.length ? <Empty title="Sin expedientes" /> : (
          <div className="overflow-x-auto"><Table>
            <TableHeader><TableRow><TableHead>Folio</TableHead><TableHead>Trámite</TableHead><TableHead className="hidden md:table-cell">Titular</TableHead><TableHead className="hidden md:table-cell">Derechos</TableHead><TableHead className="hidden md:table-cell">Fecha</TableHead><TableHead>Estatus</TableHead></TableRow></TableHeader>
            <TableBody>{list.data.map((e) => (
              <TableRow key={e.folio} className="cursor-pointer hover:bg-secondary/60" onClick={() => setSel(e.folio)} data-testid={`intranet-expediente-row-${e.folio}`}>
                <TableCell className="font-mono text-xs">{e.folio}</TableCell><TableCell className="text-sm">{e.tramite_label} {e.is_demo && <DemoBadge />}</TableCell>
                <TableCell className="hidden md:table-cell text-sm">{e.user_name}</TableCell><TableCell className="hidden md:table-cell font-mono text-sm">{money(e.cost)}</TableCell>
                <TableCell className="hidden md:table-cell text-xs">{fdate(e.created_at)}</TableCell><TableCell><StatusBadge status={e.status} /></TableCell>
              </TableRow>
            ))}</TableBody>
          </Table></div>
        )}
      </CardContent></Card>

      <Sheet open={!!cur} onOpenChange={(o) => !o && setSel(null)}>
        <SheetContent className="bg-white w-full sm:max-w-lg overflow-y-auto" data-testid="intranet-expediente-sheet">
          {cur && (
            <>
              <SheetHeader><SheetTitle>{cur.tramite_label}</SheetTitle></SheetHeader>
              <div className="flex items-center gap-2 mt-1"><span className="font-mono text-xs">{cur.folio}</span><StatusBadge status={cur.status} testid="intranet-expediente-status" /></div>
              <div className="mt-4 space-y-2">
                <Row label="Titular"><span className="text-sm">{cur.user_name} · {cur.user_email}</span></Row>
                {Object.entries(cur.fields || {}).map(([k, v]) => <Row key={k} label={k.replace(/_/g, " ")}><span className="text-sm">{v}</span></Row>)}
                {cur.documents?.map((d) => <Row key={d.doc_id} label={`${d.name} (${(d.size / 1024).toFixed(0)} KB)`}><Hash value={d.sha256} n={8} /></Row>)}
              </div>
              {cur.status === "en_revision" && (
                <div className="mt-5 space-y-2 rounded-lg border p-3">
                  <Textarea rows={2} placeholder="Observaciones (obligatorio al rechazar)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="expediente-note-input" />
                  <div className="flex gap-2">
                    <Button className="flex-1" onClick={() => act("aprobar")} disabled={busy} data-testid="expediente-approve-button">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <PenLine className="h-4 w-4 mr-1" />}Aprobar y firmar</Button>
                    <Button variant="outline" onClick={() => act("rechazar")} disabled={busy || !note} data-testid="expediente-reject-button"><XCircle className="h-4 w-4 mr-1" />Rechazar</Button>
                  </div>
                </div>
              )}
              {cur.status === "emitido" && <div className="mt-5"><LegalPanel data_hash={cur.data_hash} nom151={cur.nom151} anchor={cur.anchor} signature={cur.signature} /></div>}
              <div className="mt-5"><Timeline items={cur.timeline} labels={TL} /></div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
